'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { Eyebrow } from '@loci/ui';
import { lociCltDark } from '@/lib/map-styles/loci-clt-dark';
import { formatCoord } from '@/utils/formatCoord';

// Read NEXT_PUBLIC_* directly from process.env. Importing `@/config/env` would
// pull `@loci/config` (and its winston/fs deps) into the client bundle,
// which Next.js cannot resolve. Next.js inlines `NEXT_PUBLIC_*` at build time,
// so this is the supported pattern for client components — see GoogleMap.
function getPublicGoogleMapsApiKey(): string {
  return process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ?? '';
}

export interface EventLocusMapProps {
  /** Event coordinate latitude. Required to render the mini-map. */
  latitude: number;
  /** Event coordinate longitude. Required to render the mini-map. */
  longitude: number;
  /** Event id used to deep-link into `/map?eventId={id}`. */
  eventId: string;
}

// LOCI Electric Amber — matches the amber pill + accent tokens used across
// the event-detail surface. WEB-1065: the theme-invariant token carries the
// exact value that shipped as a literal (#FFB938); SVG `stroke` resolves var().
const AMBER = 'var(--loci-locus-amber)';

/**
 * Build a Google Static Maps URL with a style payload that echoes the
 * `lociCltDark` Google Maps JSON — same hue family as the D2 `/map` canvas
 * without shipping another map SDK.
 */
function buildStaticMapUrl(
  latitude: number,
  longitude: number,
  sizePx: number,
  apiKey: string,
): string {
  const base = 'https://maps.googleapis.com/maps/api/staticmap';
  const params = new URLSearchParams({
    center: `${latitude},${longitude}`,
    zoom: '15',
    scale: '2',
    size: `${sizePx}x${sizePx}`,
    maptype: 'roadmap',
    key: apiKey,
  });

  const styleParams = lociCltDark
    .map(({ featureType, elementType, stylers }) => {
      const segments: string[] = [];
      if (featureType) segments.push(`feature:${featureType}`);
      if (elementType) segments.push(`element:${elementType}`);
      for (const styler of stylers ?? []) {
        for (const [key, value] of Object.entries(styler)) {
          // Static Maps expects color values as `0xrrggbb` (not `#rrggbb`).
          const normalized =
            key === 'color' && typeof value === 'string' && value.startsWith('#')
              ? `0x${value.slice(1)}`
              : String(value);
          segments.push(`${key}:${normalized}`);
        }
      }
      return segments.join('|');
    })
    .filter(Boolean);

  let url = `${base}?${params.toString()}`;
  for (const style of styleParams) {
    url += `&style=${encodeURIComponent(style)}`;
  }
  return url;
}

/**
 * Lazy-loaded mini-map that opens the full `/map` surface with the event
 * pre-selected (FRE-754). Uses a single Google Static Maps tile plus an
 * SVG/HTML overlay to avoid shipping a second Google Maps JS instance on
 * the event detail page.
 */
export function EventLocusMap({ latitude, longitude, eventId }: EventLocusMapProps) {
  const containerRef = useRef<HTMLAnchorElement | null>(null);
  const [hasEntered, setHasEntered] = useState(false);
  const [sizePx, setSizePx] = useState(240);

  // Track desktop breakpoint (matches Tailwind's `tablet:` at 768px) so we
  // can request a sharper static tile on larger screens without doubling
  // the mobile payload.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(min-width: 768px)');
    const update = () => setSizePx(mql.matches ? 320 : 240);
    update();
    mql.addEventListener?.('change', update);
    return () => mql.removeEventListener?.('change', update);
  }, []);

  // IntersectionObserver-driven lazy load — keeps the static tile off the
  // critical path so above-the-fold LCP is untouched.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setHasEntered(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setHasEntered(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const apiKey = getPublicGoogleMapsApiKey();
  const staticMapUrl = useMemo(() => {
    if (!hasEntered || !apiKey) return null;
    return buildStaticMapUrl(latitude, longitude, sizePx, apiKey);
  }, [hasEntered, apiKey, latitude, longitude, sizePx]);

  const coordLabel = `${formatCoord(latitude)}, ${formatCoord(longitude)}`;

  return (
    <section
      aria-label="Event location"
      data-testid="event-locus-map"
      className="flex flex-col gap-3"
    >
      <Eyebrow>THE LOCUS</Eyebrow>

      {/* Plain <a> rather than next/link — the full /map surface is a heavy
          client route, so Link prefetch is wasted effort here. A direct
          anchor also keeps the intersection-observer ref simple. The teal →
          navy gradient background is a Tailwind arbitrary value rather than
          an inline style; it matches the `lociCltDark` ambient feel and
          shows before the tile enters the viewport (or whenever the Static
          Maps API key is unavailable). */}
      <a
        ref={containerRef}
        href={`/map?eventId=${encodeURIComponent(eventId)}`}
        aria-label="Open full map with this event selected"
        data-testid="event-locus-map-link"
        className="relative block overflow-hidden rounded-xl border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 w-[240px] h-[240px] tablet:w-[320px] tablet:h-[320px] bg-[linear-gradient(135deg,var(--loci-fixed-skyline)_0%,var(--loci-locus-map-mid)_60%,var(--loci-indigo)_100%)]"
      >
        {staticMapUrl && (
          <Image
            src={staticMapUrl}
            alt=""
            aria-hidden="true"
            data-testid="event-locus-map-tile"
            width={sizePx}
            height={sizePx}
            unoptimized
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}

        {/* Dashed amber coordinate ring — stroked SVG scales with the container. */}
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid meet"
          className="absolute inset-0 h-full w-full pointer-events-none"
          aria-hidden="true"
        >
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            stroke={AMBER}
            strokeOpacity="0.7"
            strokeWidth="0.8"
            strokeDasharray="2 2"
          />
        </svg>

        {/* Amber pin + pulse halo — HTML div so Tailwind's motion-safe
            utilities handle the prefers-reduced-motion gate for us. Amber
            fill + opacity are Tailwind arbitrary utilities instead of
            inline styles, per the app-no-inline-styles rule. */}
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="relative h-3 w-3">
            <span
              data-testid="event-locus-map-pulse"
              className="absolute inset-0 rounded-full motion-safe:animate-ping bg-(--loci-locus-amber)/40"
            />
            <span className="absolute inset-0 rounded-full ring-2 ring-white/60 bg-(--loci-locus-amber)" />
          </div>
        </div>

        {/* Coord label — mono, 4 decimals, pinned to the bottom edge. */}
        <span
          data-testid="event-locus-map-coords"
          className="absolute inset-x-0 bottom-0 px-3 py-2 font-mono text-micro leading-none text-white/90 bg-black/40 backdrop-blur-sm text-center"
        >
          {coordLabel}
        </span>
      </a>
    </section>
  );
}
